// modules
import FormatService from 'modules/Format';
import Fetch from 'modules/Fetch';
import SearchService from 'modules/Search';
import WsPortfolioV2Feeder from 'modules/WebSocket/modules/PortfolioV2/store';
import LoggerService from 'modules/Logger';
import LocaleService from 'modules/Localization';
import { OrderingServiceFetch } from 'modules/Ordering/services';
import AnalyticsService from 'modules/Analytics';
import PositionsService from 'modules/TradesPW3/Positions';
import LiveData from 'modules/LiveData';
// utils
import { getNonDeliveryPositions } from 'modules/TradesPW3/Positions/utils';
// constants
import { CommonOrderValidity, OrderCategory, OrderStatuses, OrderTypes, PlaceOrderProduct, Products, Sides } from 'modules/Ordering';
import { ITVOrder } from 'modules/TradingChart/components/TradingView/utils/providers/brokerProvider/orders/types';
import { broker } from 'modules/TradingChart/consts';
import { decomposeExchangeToken } from 'modules/BOD/modules/Search/utils';
import { EVENTS } from 'modules/Analytics/consts';
import {
  NotificationType,
  OrderType as TvOrderType,
  Side as TvSide,
  OrderStatusFilter as TvOrderStatusFilter,
} from '../../TradingChart/components/TradingView/sources/charting_library';
import { CancelOrderRequest, LiveOrderUpdates } from './types';
import {
  BasketErrorMessage,
  PositionBase,
  SurveillanceShownMap,
} from '../Positions/typings';
// store
import { OrdersStore } from './store';

class OrdersTVService extends OrdersStore {
  constructor() {
    super();

    WsPortfolioV2Feeder.registerHandler('order_update', this.handleOrderUpdate);
  }

  getOrdersData = async () => {
    await this.getOrders();
    return this.tvOrders;
  };

  getOrders = async () => {
    try {
      const response = await Fetch.request<'PORTFOLIO', GetOrderBookResponse>(
        'PORTFOLIO',
        '/orderbook',
        'GET_ORDERS',
        {
          query: {
            nsecom: true,
          },
        },
      );

      if (response.data?.history) {
        const upstoxOrders =
          response.data.history.filter(order => this.checkIsOrderForTv(order));

        this.setOrders(upstoxOrders);

        const formattedOrders = await this.modifyOrders(upstoxOrders);
        formattedOrders.sort((a, b) => a.time - b.time);
        this.setTvOrders(formattedOrders);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      LoggerService.log(error);
    }
  };

  private checkIsOrderForTv = (order: Order) => {
    const orderProducts = ['STG', 'GTT'];
    return !orderProducts.includes(order.product) && !order.isAMO;
  }

  protected mapOrderToTVcolumnOrder = async (
    order: Order,
  ): Promise<ITVOrder> => {
    const exchangeToken = FormatService.generateExchangeToken(
      order.instrument.e,
      order.instrument.t,
    );
    const scripData = await SearchService.searchByExchangeToken(exchangeToken);
    const digitsAfterDot = FormatService.getDigitsAfterDot(exchangeToken);
    const tvExchangeToken = FormatService.generateCustomTvExchangeToken(
      scripData?.displayName || '',
      exchangeToken || '',
    );

    const { segment } = decomposeExchangeToken(exchangeToken);

    return {
      id: order.orderNumber.oms,
      symbol: tvExchangeToken,
      name: scripData?.symbol || '',
      type: this.mapOrderTypeToTVorderType(OrderTypes[order.orderType]),
      side: this.mapOrderSideToTVside(Sides[order.side]),
      status: this.mapOrderStatusToTVstatus(order.status),
      qty: Number(order?.quantity?.total),
      duration: {
        type: order.validity === 'D' ? CommonOrderValidity.D : CommonOrderValidity.I,
      },
      customFields: {
        productType: PlaceOrderProduct[Products[order.product]],
      },
      segment,
      time: Number(order.time) / 1000,
      product: Products[order.product],
      quantityTraded: Number(order?.quantity?.traded),
      priceLimit:
        FormatService.formatToIndianFloat(
          order?.price?.limit,
          digitsAfterDot,
        ),
      priceTrigger:
        FormatService.formatToIndianFloat(
          order?.price?.trigger,
          digitsAfterDot,
        ),
      priceTraded:
        FormatService.formatToIndianFloat(
          order?.price?.avg,
          digitsAfterDot,
        ),
      orderNumberOms: order?.orderNumber?.oms,
      orderNumberExchange: order?.orderNumber?.exchange,
      customMessage: order.message,
      instrumentKey: scripData?.instrumentKey,
      // Internal TV property
      ...(
        order?.price?.limit && {
          limitPrice: FormatService.getRoundOffToTwoDecimal(
            order?.price?.limit,
            digitsAfterDot,
          ),
        }
      ),
      ...(
        order?.price?.trigger && {
          stopPrice: FormatService.getRoundOffToTwoDecimal(
            order?.price?.trigger,
            digitsAfterDot,
          ),
        }
      ),
      avgPrice: FormatService.getRoundOffToTwoDecimal(
        order?.price?.avg || 0,
        digitsAfterDot,
      ),
      filledQty: Number(order?.quantity?.traded),
    };
  };

  modifyOrders = async (ordersList: Order[]): Promise<ITVOrder[]> =>
    Promise.all(ordersList.map(order => this.mapOrderToTVcolumnOrder(order)));

  mapOrderSideToTVside = (side: Sides) =>
    (side === 'Buy' ? TvSide.Buy : TvSide.Sell);

  findOrderById = (orderId: string): Order | undefined =>
    this.orders.find(item => item.orderNumber.oms === orderId);

  mapOrderStatusToTVstatus = (
    orderStatus: OrderStatus,
  ): TvOrderStatusFilter => {
    switch (OrderStatuses[orderStatus]) {
      case OrderStatuses.O:
      case OrderStatuses.TP:
      case OrderStatuses.AMO:
        return TvOrderStatusFilter.Working;

      case OrderStatuses.COM:
        return TvOrderStatusFilter.Filled;

      case OrderStatuses.CAN:
        return TvOrderStatusFilter.Canceled;

      case OrderStatuses.R:
        return TvOrderStatusFilter.Rejected;

      default:
        return TvOrderStatusFilter.All;
    }
  };

  mapOrderTypeToTVorderType = (orderType: OrderTypes): TvOrderType => {
    switch (orderType) {
      case OrderTypes.L:
        return TvOrderType.Limit;

      case OrderTypes.M:
        return TvOrderType.Market;

      case OrderTypes.SL:
        return TvOrderType.StopLimit;

      case OrderTypes['SL-M']:
        return TvOrderType.Stop;

      default:
        return TvOrderType.Limit;
    }
  };

  mapTVorderTypeToOrderType = (
    tvOrderType: TvOrderType,
  ): OrderEntryOrderType => {
    switch (tvOrderType) {
      case TvOrderType.Limit:
        return 'L';

      case TvOrderType.Market:
        return 'M';

      case TvOrderType.StopLimit:
        return 'SL';

      case TvOrderType.Stop:
        return 'SL-M';

      default:
        return 'L';
    }
  };

  handleOrderUpdate: WsHandler<LiveOrderUpdates> = async message => {
    if (message.data) {
      const { data: updatedOrder } = message;
      const formattedOrders = await this.modifyOrders([updatedOrder.data]);
      const isOrderForTv = this.checkIsOrderForTv(updatedOrder.data);

      const apiError = formattedOrders[0].customMessage;
      const orderStatus = formattedOrders[0].status;
      formattedOrders[0].status =
        orderStatus !== TvOrderStatusFilter.Rejected && apiError
          ? TvOrderStatusFilter.Rejected
          : formattedOrders[0].status;
      const errorMessage =
        apiError === LocaleService.translate('API_AMO_ERROR_MESSAGE')
          ? LocaleService.translate('AMO_ERROR_MESSAGE')
          : apiError || LocaleService.translate('ORDER_REJECTED_ERROR_MESSAGE');

      if (formattedOrders[0].status === TvOrderStatusFilter.Rejected) {
        if (broker.instance) {
          broker.instance.showNotification(
            LocaleService.translate('ORDER_REJECTED'),
            errorMessage,
            NotificationType.Error,
          );
        }
      }

      const indexOfUpdatedTvOrder = this.tvOrders?.findIndex(
        ({ orderNumberOms }) =>
          orderNumberOms === formattedOrders[0].orderNumberOms,
      );

      if (indexOfUpdatedTvOrder !== -1) {
        this.updateTvOrders(formattedOrders[0], indexOfUpdatedTvOrder);
      } else if (isOrderForTv) {
        this.addTvOrder(formattedOrders[0]);
      }

      const indexOfUpdatedOrder = this.orders?.findIndex(
        ({ orderNumber: { oms } }) =>
          oms === updatedOrder.data.orderNumber.oms,
      );

      if (indexOfUpdatedOrder !== -1) {
        this.updateOrders(updatedOrder.data, indexOfUpdatedOrder);
      } else if (isOrderForTv) {
        this.addOrder(updatedOrder.data);
      }

      if (broker.instance) {
        broker.instance.updateOrders(this.tvOrders);
      }
    }
  };

  // For future to exit all positions without opening Positions MTM modal
  placeExitAllMtmPositions = (
    positions: PositionBase[],
    isSurveillanceShownMap: SurveillanceShownMap,
  ) => {
    const positionsToExit = getNonDeliveryPositions(positions);
    PositionsService.multiPositionSquareOff(
      positionsToExit,
      isSurveillanceShownMap,
    );
    AnalyticsService.sendEvent(EVENTS.POSITIONS_MTM_EXIT_ALL_EXECUTED);
  };

  addLTPtoOrder = (orders: CommonOrder[]) => {
    const updatedOrders: CommonOrder[] = orders.map((order) => {
      const liveData = LiveData.getByInstrumentKey(order.instrumentKey || '');
      return {
        ...order,
        additionalInfo: {
          ...(order?.additionalInfo || {}),
          orderLTP: Number(liveData?.lastTradedPrice),
        },
      };
    });
    return updatedOrders;
  }

  placeBasketOrder = async (
    exitAllFetchRequestData: CommonOrdersRequest,
  ) => {
    try {
      const updatedOrders = this.addLTPtoOrder(exitAllFetchRequestData.orders);
      const updatedExitAllFetchRequestData = {
        ...exitAllFetchRequestData,
        orders: updatedOrders,
      };
      const response = await
        OrderingServiceFetch.placeOrderRequest(updatedExitAllFetchRequestData);
      const failedOrderItems = this.getFailedBasketOrderItems(
        response?.data?.responseDetailList as CommonOrdersResponse['responseDetailList']);

      if (failedOrderItems.length) {
        const messages = await this.getFailedBasketOrderMessages(
          failedOrderItems,
          updatedExitAllFetchRequestData.orders,
        );
        this.handleFailedBasketOrdersErrors(messages);
      }

      if (response.error && broker.instance) {
        broker.instance.showNotification(
          LocaleService.translate('ORDER_FAILED'),
          response.error.message,
          NotificationType.Error,
        );
      }
    } catch (error: any) {
      if (broker.instance) {
        broker.instance.showNotification(
          LocaleService.translate('ORDER_FAILED'),
          LocaleService.translate('POSITIONS_MTM_EXIT_FAILURE'),
          NotificationType.Error,
        );
      }
    }
  };

  getFailedBasketOrderItems = (
    basketOrderResultData: CommonOrdersResponse['responseDetailList'],
  ) => {
    const failedOrderItems = Array.isArray(
      basketOrderResultData,
    )
      ? basketOrderResultData.filter(
          order => order.status === 'R',
        )
      : [];
    return failedOrderItems;
  }

  getFailedBasketOrderMessages = async (
    failedOrderItems: CommonOrdersResponse['responseDetailList'],
    basketOrders: CommonOrder[],
  ) => {
    const messages: BasketErrorMessage[] = [];

    const failedOrderMap = new Map(failedOrderItems?.map(
      (orderItem) => [orderItem.itemId, orderItem.message],
    ));

    await Promise.all(
      basketOrders.map(async (basketOrder) => {
        const errorMessage = failedOrderMap.get(
          basketOrder.additionalInfo?.itemId || '',
        );

        if (errorMessage) {
          const { instrumentKey } = basketOrder;
          const instrument = await
            SearchService.searchByInstrumentKey(instrumentKey);
          const symbol = instrument?.displayName || '';

          messages.push({
            text: `${symbol}: ${errorMessage}`,
            id: basketOrder.additionalInfo?.itemId || '',
          });
        }
      }),
    );

    return messages;
  }

  handleFailedBasketOrdersErrors = (
    messages: BasketErrorMessage[],
  ) => {
    messages.forEach((message, index) => {
      const { text } = message;
      setTimeout(() => {
        if (broker.instance) {
          broker.instance.showNotification(
            LocaleService.translate('ORDER_FAILED'),
            text,
            NotificationType.Error,
          );
        }
      }, index * 300);
    });
  }

  getOrderCategory = (productType?: Product) => {
    if (productType === 'CO') {
      return OrderCategory.CO;
    }

    if (productType === 'OCO') {
      return OrderCategory.OCO;
    }

    return OrderCategory.REGULAR;
  }

  generateMultiCancelOrdersPayload = (orderIds: string[]) => {
    const cancelOrdersPayload: CancelOrderRequest[] = [];
    orderIds.forEach((orderId) => {
      const order = this.findOrderById(orderId);
      const orderCategory = this.getOrderCategory(order?.product);
      if (order) {
        cancelOrdersPayload.push({
          omsId: order?.orderNumber?.oms,
          upstoxId: order?.orderNumber?.orderRefId,
          productType: order?.product,
          instrumentKey: order.instrument_key,
          amo: !!order?.isAMO,
          tsl: !!order?.orderNumber?.orderRefId?.includes('TSL'),
          orderCategory,
        });
      }
    });
    return cancelOrdersPayload;
  }

  cancelMultiOrders = async (orderIds: string[]) => {
    try {
      const cancelOrdersPayload = this.generateMultiCancelOrdersPayload(
        orderIds,
      );
      const response = await OrderingServiceFetch.cancelOrderRequest(
        cancelOrdersPayload,
      );

      if (response.error) {
        if (broker.instance) {
          broker.instance.showNotification(
            LocaleService.translate('CANCEL_ORDER'),
            response?.error?.message,
            NotificationType.Error,
          );
        }
      }
    } catch (error: any) {
      if (broker.instance) {
        broker.instance.showNotification(
          LocaleService.translate('CANCEL_ORDER'),
          LocaleService.translate('CANCEL_ORDER_FAILED'),
          NotificationType.Error,
        );
      }
    }
  };

  mapStatus = (status: string, isAMO: boolean): MappedStatus => {
    if (isAMO) {
      return 'AMO';
    }
    switch (status) {
      case 'TP':
      case 'L':
      case 'O':
      case 'COM':
      case 'R':
      case 'CAN':
      case 'INACTIVE':
      case 'SCHEDULED':
        return OrderStatuses[status];
      case 'POR':
        return OrderStatuses.SCHEDULED;
      case 'TRIGGERED':
        return OrderStatuses.O;
      case 'P':
      default:
        return OrderStatuses.P;
    }
  };
}

const ordersTVService = new OrdersTVService();
export default ordersTVService;
